import { type OpenShift } from "@clipboard-health/contract-worker-app-bff";
import { getTheme } from "@clipboard-health/ui-theme";
import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import { BookabilityDecision } from "@src/appV2/OpenShifts/ShiftAction";
import { useCallback, useEffect, useMemo } from "react";

import { MANDATORY_BREAK_DURATION_IN_MINUTES } from "../../Shift/constants";
import { convertOpenShiftToModalShiftData } from "../../Shift/convertOpenShiftToModalShiftData";
import { useGetShiftModal } from "../../Shift/Open/useGetShiftModal";
import { useFilteredListViewOpenShiftsData } from "../ListView/useFilteredListViewOpenShiftsData";
import { useShiftModalsDataContext } from "../useShiftModalsDataContext";
import { AnimatedShiftRecommendationsWrapper } from "./AnimatedWrapper";
import { ShiftRecommendationsCard } from "./Card";
import { useShiftRecommendationsNavigation } from "./hooks/useShiftRecommendationsNavigation";
import { ShiftRecommendationsOverlay } from "./Overlay";

export interface ShiftRecommendationsPageProps {
  modalState: {
    closeModal: () => void;
  };
}

export function ShiftRecommendationsPage(props: ShiftRecommendationsPageProps) {
  const { modalState } = props;

  const {
    data: { filteredOpenShifts, workplacesMap, shiftAuctionsMap },
    isLoading: openShiftsIsLoading,
    isSuccess: openShiftsIsSuccess,
  } = useFilteredListViewOpenShiftsData();

  const { setInitialShiftModalsData, shiftSlots, friendsMap, outboundFriendRequestsSet } =
    useShiftModalsDataContext();

  const recommendedShifts = useMemo(() => {
    if (!openShiftsIsSuccess) {
      return [];
    }

    return filteredOpenShifts.filter((shift) => shift.type === "open-shift").slice(0, 5);
  }, [openShiftsIsSuccess, filteredOpenShifts]);

  const {
    currentIndex,
    previousIndex,
    currentShift,
    goNext,
    goPrevious,
    reset,
    progress,
    pause,
    resume,
  } = useShiftRecommendationsNavigation({
    recommendedShifts,
    onClose: modalState.closeModal,
    autoProgressDuration: 5000,
  });

  useEffect(() => {
    reset();
  }, [reset]);

  const currentWorkplace = currentShift
    ? workplacesMap.get(currentShift.relationships.workplace.data.id)
    : undefined;

  const {
    data: { offer: currentOffer },
    isLoading: isOfferLoading,
  } = useGetShiftModal(currentShift?.id, {
    enabled: !!currentShift,
    autoRefreshOffer: true,
  });

  useEffect(() => {
    if (currentShift && currentWorkplace && currentOffer && !isOfferLoading) {
      const shiftAuctionId = currentShift.relationships.shiftAuction?.data.id;
      const shiftAuction = shiftAuctionId ? shiftAuctionsMap.get(shiftAuctionId) : undefined;

      const modalShiftData = convertOpenShiftToModalShiftData({
        shift: currentShift,
        workplace: currentWorkplace,
        offer: currentOffer,
        rateNegotiation: undefined,
        mandatoryBreakDurationInMinutes: MANDATORY_BREAK_DURATION_IN_MINUTES,
        shiftAuction,
      });

      setInitialShiftModalsData({
        shift: modalShiftData,
        bookabilityStatus: {
          type: "shift",
          id: currentShift.id,
          attributes: {
            bookability: {
              decision: BookabilityDecision.ALLOWED,
            },
          },
        },
        hasHoursRestrictionConflict: false,
        blockingRequirementsContext: undefined,
        shiftSlots: undefined,
        friendsMap: new Map(),
        recentColleaguesMap: new Map(),
        outboundFriendRequestsSet: new Set(),
      });
    }
  }, [
    currentShift,
    currentWorkplace,
    currentOffer,
    isOfferLoading,
    setInitialShiftModalsData,
    currentIndex,
    shiftAuctionsMap,
  ]);

  const createStoryCard = useCallback(
    (shift: OpenShift, index: number, currentIndex: number) => {
      const storyWorkplace = workplacesMap.get(shift.relationships.workplace.data.id);
      if (!storyWorkplace) {
        return null;
      }

      return (
        <AnimatedShiftRecommendationsWrapper
          key={shift.id}
          currentIndex={currentIndex}
          previousIndex={previousIndex}
          index={index}
          onClick={goNext}
        >
          <ShiftRecommendationsCard
            storyShift={shift}
            storyWorkplace={storyWorkplace}
            shiftSlots={shiftSlots || {}}
            friendsMap={friendsMap}
            outboundFriendRequestsSet={outboundFriendRequestsSet}
          />
        </AnimatedShiftRecommendationsWrapper>
      );
    },
    [workplacesMap, previousIndex, goNext, shiftSlots, friendsMap, outboundFriendRequestsSet]
  );

  if (openShiftsIsLoading || !openShiftsIsSuccess) {
    return null;
  }

  if (recommendedShifts.length === 0) {
    return null;
  }

  if (!currentShift || !currentWorkplace) {
    return null;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <CssBaseline />
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "black",
          zIndex: 9999,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            position: "relative",
          }}
        >
          {recommendedShifts.map((shift, index) => createStoryCard(shift, index, currentIndex))}
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            zIndex: 20_000,
          }}
        >
          <ShiftRecommendationsOverlay
            currentIndex={currentIndex}
            totalStories={recommendedShifts.length}
            currentProgress={progress}
            onNext={goNext}
            onPrevious={goPrevious}
            onPause={pause}
            onResume={resume}
          />
        </Box>
      </Box>
    </ThemeProvider>
  );
}
